import { useState, useEffect } from 'react'
import Swal from 'sweetalert2'
import Layout from '../../components/Layout'
import Toast from '../../utils/toast'
import {
  createMultiLogin,
  getMultiLoginList,
  setMultiLoginActive,
  changeMultiLoginPassword,
  deleteMultiLogin
} from '../../apiservices/multiLoginApi'
import ExecutivePermissionsModal from './ExecutivePermissionsModal'

import './style.css'

/**
 * The 23 grantable privileges as [key, label]. Single source for both the
 * create-account checkboxes and the edit modal. Keys are byte-identical with
 * d99-admin/src/utils/permissions.js and the backend requirePermission(...).
 */
const PRIVILEGE_FIELDS = [
  ['dashboard', 'DashBoard'],
  ['marketAnalysis', 'Market Analysis'],
  ['userList', 'User List'],
  ['insertUser', 'Insert User'],
  ['accountStatement', 'Account Statement'],
  ['partyWinLoss', 'Party Win Loss'],
  ['currentBets', 'Current Bets'],
  ['generalLock', 'General Lock'],
  ['casinoResult', 'Casino Result'],
  ['liveCasinoResult', 'Live Casino Result'],
  ['ourCasino', 'Our Casino'],
  ['events', 'Events'],
  ['marketSearchAnalysis', 'Market Search Analysis'],
  ['loginUserCreation', 'Login User creation'],
  ['withdraw', 'Withdraw'],
  ['deposit', 'Deposit'],
  ['creditReference', 'Credit Reference'],
  ['userInfo', 'User Info'],
  ['userPasswordChange', 'User Password Change'],
  ['userLock', 'User Lock'],
  ['betLock', 'Bet Lock'],
  ['activeUser', 'active user'],
  ['agentAssign', 'Agent Assign']
]

// Ek row ki permission value — purani rows jo label ke naam se store hui thin
// (key-contract se pehle) unke ticks bhi dikhte rahen
const permValue = (perms, key, label) => !!(perms?.[key] ?? perms?.[label])

const buildInitialPrivileges = () => {
  const p = { all: false }
  PRIVILEGE_FIELDS.forEach(([k]) => {
    p[k] = false
  })
  p.dashboard = true // default checked
  return p
}

function MultiLogin() {
  const [formData, setFormData] = useState({
    clientId: '',
    fullName: '',
    password: '',
    confirmPassword: '',
    mpass: ''
  })

  const [privileges, setPrivileges] = useState(buildInitialPrivileges)
  const [loading, setLoading] = useState(false)
  const [executives, setExecutives] = useState([])
  const [editing, setEditing] = useState(null)
  const [errors, setErrors] = useState({})

  useEffect(() => {
    fetchExecutives()
  }, [])

  const fetchExecutives = async () => {
    try {
      const res = await getMultiLoginList()
      if (!res?.success) {
        Toast.fire({ icon: 'error', title: 'Failed to load multi-login accounts' })
        return
      }
      setExecutives(res.executives || [])
    } catch (err) {
      Toast.fire({
        icon: 'error',
        title: err?.response?.data?.message || 'Error fetching multi-login accounts'
      })
    }
  }

  /**
   * Field-wise validation — dexch ke Multi Login wale hi texts aur rules.
   * '' matlab field theek hai.
   */
  const validateField = (name, value, all = formData) => {
    switch (name) {
      case 'clientId':
        if (!value) return 'The Client ID field is required'
        if (value.length < 4) return 'The Client ID field must be at least 4 characters'
        return ''
      case 'fullName':
        if (!value) return 'The Full Name field is required'
        if (value.length < 4) return 'The Full Name field must be at least 4 characters'
        return ''
      case 'password':
        if (!value) return 'The Password field is required'
        if (value.length < 8) return 'The Password field must be at least 8 characters'
        if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) return 'The password must contain at least: 1 uppercase letter, 1 lowercase letter, 1 number'
        return ''
      case 'confirmPassword':
        if (!value) return 'The Confirm Password field is required'
        if (value !== all.password) return 'The Confirm Password confirmation does not match'
        return ''
      case 'mpass':
        // Sirf red outline — is field ke neeche message nahi dikhta
        if (!value) return 'required'
        return ''
      default:
        return ''
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    const next = { ...formData, [name]: value }
    setFormData(next)
    setErrors((prev) => {
      const updated = { ...prev, [name]: validateField(name, value, next) }
      // Password badalne se confirm-password ka match ban ya bigad sakta hai
      if (name === 'password' && next.confirmPassword) {
        updated.confirmPassword = validateField('confirmPassword', next.confirmPassword, next)
      }
      return updated
    })
  }

  // Blur pe bhi validate — field chhodte hi (chahe chhua na ho) mark ho jaata hai
  const handleBlur = (e) => {
    const { name, value } = e.target
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }))
  }

  // Error hone par red outline; sirf Client ID valid hone par green dikhta hai
  const fieldClass = (name) => {
    if (errors[name]) return 'form-control is-invalid'
    if (name === 'clientId' && errors[name] === '') return 'form-control is-valid'
    return 'form-control'
  }

  const handlePrivilegeChange = (key) => {
    let next
    if (key === 'all') {
      const val = !privileges.all
      next = { all: val }
      PRIVILEGE_FIELDS.forEach(([k]) => {
        next[k] = val
      })
    } else {
      next = { ...privileges, [key]: !privileges[key] }
      next.all = PRIVILEGE_FIELDS.every(([k]) => next[k])
    }
    setPrivileges(next)
    // Checkbox chhedne par privileges bhi validate hote hain — aakhri wala
    // uncheck karte hi "The Privileges field is required" dikhta hai
    const anyChecked = PRIVILEGE_FIELDS.some(([k]) => next[k])
    setErrors((prev) => ({ ...prev, plist: anyChecked ? '' : 'The Privileges field is required' }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (loading) return

    // Sabhi fields ek saath mark hote hain (dexch jaisa), phir ek generic toast
    const nextErrors = {}
    for (const field of ['clientId', 'fullName', 'password', 'confirmPassword']) {
      const msg = validateField(field, formData[field])
      if (msg) nextErrors[field] = msg
    }
    // Bina kisi privilege ke account banane ka matlab nahi — wo login karte hi
    // /no-access pe atak jayega
    if (!PRIVILEGE_FIELDS.some(([k]) => privileges[k])) {
      nextErrors.plist = 'The Privileges field is required'
    }
    if (!formData.mpass) nextErrors.mpass = 'required'
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) {
      Toast.fire({ icon: 'error', title: 'Please fill required fields' })
      return
    }

    // Build the permission map from the real privilege keys (drop the "all" helper).
    const permissions = {}
    PRIVILEGE_FIELDS.forEach(([k]) => {
      permissions[k] = !!privileges[k]
    })

    setLoading(true)
    try {
      // /executive/create verifyTransactionPassword ke peeche hai — transactionPassword
      // body me na ho to middleware wahin reject kar deta hai. full_name bhi server
      // leta hai (executiveController createExecutive), table isi se bharti hai.
      const res = await createMultiLogin({
        username: formData.clientId,
        full_name: formData.fullName,
        password: formData.password,
        transactionPassword: formData.mpass,
        permissions
      })

      if (res?.success) {
        Toast.fire({ icon: 'success', title: res.message || 'Multi Login account created' })
        handleReset()
        fetchExecutives()
      } else {
        Toast.fire({ icon: 'error', title: res?.message || 'Failed to create account' })
      }
    } catch (error) {
      Toast.fire({
        icon: 'error',
        title: error?.response?.data?.message || 'Failed to create account'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setFormData({ clientId: '', fullName: '', password: '', confirmPassword: '', mpass: '' })
    setPrivileges(buildInitialPrivileges())
    setErrors({})
  }

  const handleToggleActive = async (exec) => {
    try {
      // `active` omit karo — server khud toggle karta hai (setExecutiveActive),
      // to stale row state se galat value nahi jaati
      const res = await setMultiLoginActive(exec.id)
      if (res?.success) {
        Toast.fire({ icon: 'success', title: res.message })
        fetchExecutives()
      } else {
        Toast.fire({ icon: 'error', title: res?.message || 'Failed to update status' })
      }
    } catch (err) {
      Toast.fire({ icon: 'error', title: err?.response?.data?.message || 'Failed to update status' })
    }
  }

  const handleResetPassword = async (exec) => {
    const { value: password } = await Swal.fire({
      title: `Reset password — ${exec.username}`,
      input: 'password',
      inputLabel: 'New password',
      inputPlaceholder: 'Enter new password',
      showCancelButton: true,
      confirmButtonText: 'Update',
      inputValidator: (v) => (!v || v.length < 4 ? 'Minimum 4 characters' : undefined)
    })
    if (!password) return
    try {
      const res = await changeMultiLoginPassword(exec.id, password)
      Toast.fire({
        icon: res?.success ? 'success' : 'error',
        title: res?.message || (res?.success ? 'Password updated' : 'Failed')
      })
    } catch (err) {
      Toast.fire({ icon: 'error', title: err?.response?.data?.message || 'Failed to update password' })
    }
  }

  const handleDelete = async (exec) => {
    const result = await Swal.fire({
      title: `Delete ${exec.username}?`,
      text: 'This removes the multi-login account permanently.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#d33'
    })
    if (!result.isConfirmed) return
    try {
      const res = await deleteMultiLogin(exec.id)
      if (res?.success) {
        Toast.fire({ icon: 'success', title: res.message })
        fetchExecutives()
      } else {
        Toast.fire({ icon: 'error', title: res?.message || 'Failed to delete' })
      }
    } catch (err) {
      Toast.fire({ icon: 'error', title: err?.response?.data?.message || 'Failed to delete' })
    }
  }

  return (
    <Layout title="Multi Login Account - Diamond Admin">
      <div className="multi-login-page">
        <h4 className="mb-0 page-title-box">Multi Login Account</h4>
        <div className="l">
          <div className="account-list-multi-login">
            {/* Form Section */}
            <div className="row">
              <div className="col-12">
                <form onSubmit={handleSubmit}>
                  <div className="create-account-form">
                    {/* Personal Information */}
                    <div className="row mb-4">
                      <div className="col-12">
                        <h5 className="mb-3">Personal Information</h5>
                      </div>
                      <div className="col-md-3 mb-3">
                        <label htmlFor="clientId">Client ID</label>
                        <input
                          type="text"
                          id="clientId"
                          name="clientId"
                          className={fieldClass('clientId')}
                          aria-required="true"
                          aria-invalid={errors.clientId ? 'true' : 'false'}
                          value={formData.clientId}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        {errors.clientId && <small className="error">{errors.clientId}</small>}
                      </div>
                      <div className="col-md-3 mb-3">
                        <label htmlFor="fullName">Full Name</label>
                        <input
                          type="text"
                          id="fullName"
                          name="fullName"
                          className={fieldClass('fullName')}
                          aria-required="true"
                          aria-invalid={errors.fullName ? 'true' : 'false'}
                          value={formData.fullName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        {errors.fullName && <small className="error">{errors.fullName}</small>}
                      </div>
                      <div className="col-md-3 mb-3">
                        <label htmlFor="password">Password</label>
                        <input
                          type="password"
                          id="password"
                          name="password"
                          className={fieldClass('password')}
                          aria-required="true"
                          aria-invalid={errors.password ? 'true' : 'false'}
                          value={formData.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        {errors.password && <small className="error">{errors.password}</small>}
                      </div>
                      <div className="col-md-3 mb-3">
                        <label htmlFor="confirmPassword">Confirm Password</label>
                        <input
                          type="password"
                          id="confirmPassword"
                          name="confirmPassword"
                          className={fieldClass('confirmPassword')}
                          aria-required="true"
                          aria-invalid={errors.confirmPassword ? 'true' : 'false'}
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        {errors.confirmPassword && <small className="error">{errors.confirmPassword}</small>}
                      </div>
                    </div>

                    {/* Privileges */}
                    <div className="mt-2 previlages">
                      <h5 className="mb-0">Privileges</h5>
                      <div className="previlage-box">
                        <div className="previlage-item">
                          <div className="custom-control custom-checkbox">
                            <input
                              type="checkbox"
                              className="custom-control-input"
                              id="privilege-all"
                              checked={privileges.all}
                              onChange={() => handlePrivilegeChange('all')}
                            />
                            <label className="custom-control-label" htmlFor="privilege-all">
                              All
                            </label>
                          </div>
                        </div>
                        {PRIVILEGE_FIELDS.map(([key, label]) => (
                          <div className="previlage-item" key={key}>
                            <div className={`custom-control custom-checkbox${errors.plist ? ' is-invalid' : ''}`}>
                              <input
                                type="checkbox"
                                className="custom-control-input"
                                id={`privilege-${key}`}
                                checked={!!privileges[key]}
                                onChange={() => handlePrivilegeChange(key)}
                              />
                              <label className="custom-control-label" htmlFor={`privilege-${key}`}>
                                {label}
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                      {errors.plist && <small className="error">{errors.plist}</small>}

                      <div className="previlage-master mt-2">
                        <div className="form-group mb-0">
                          <input
                            type="password"
                            name="mpass"
                            placeholder="Transaction Code"
                            className={`form-control mpass-text${errors.mpass ? ' is-invalid' : ''}`}
                            aria-required="true"
                            aria-invalid={errors.mpass ? 'true' : 'false'}
                            value={formData.mpass}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <button type="submit" className="btn btn-success" disabled={loading}>
                            {loading ? 'Submitting...' : 'Submit'}
                          </button>
                          <button type="button" id="reset" className="btn btn-light" onClick={handleReset}>
                            Reset
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>

                {/* Management Table */}
                <div className="row mt-4">
                  <div className="col-12">
                    <div className="table-responsive">
                      <table className="table b-table table-bordered">
                        <thead>
                          <tr>
                            <th>Action</th>
                            <th>Username</th>
                            <th>Status</th>
                            <th>Full Name</th>
                            {PRIVILEGE_FIELDS.map(([key, label]) => (
                              <th key={key}>{label}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {executives.length === 0 ? (
                            <tr>
                              <td colSpan={4 + PRIVILEGE_FIELDS.length} className="text-center">
                                No data available
                              </td>
                            </tr>
                          ) : (
                            executives.map((exec) => {
                              const p = exec.permissions || {}
                              return (
                                <tr key={exec.id}>
                                  <td style={{ whiteSpace: 'nowrap' }}>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary mr-1"
                                      onClick={() => setEditing(exec)}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      className={`btn btn-sm mr-1 ${exec.active ? 'btn-warning' : 'btn-success'}`}
                                      onClick={() => handleToggleActive(exec)}
                                    >
                                      {exec.active ? 'Disable' : 'Enable'}
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-info mr-1"
                                      onClick={() => handleResetPassword(exec)}
                                    >
                                      Password
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-danger"
                                      onClick={() => handleDelete(exec)}
                                    >
                                      Delete
                                    </button>
                                  </td>
                                  <td>{exec.username}</td>
                                  <td>
                                    <span className={exec.active ? 'text-success' : 'text-danger'}>
                                      {exec.active ? 'Active' : 'Inactive'}
                                    </span>
                                  </td>
                                  <td>{exec.full_name || exec.email || '-'}</td>
                                  {PRIVILEGE_FIELDS.map(([key, label]) => (
                                    <td key={key}>{permValue(p, key, label) ? '✓' : '-'}</td>
                                  ))}
                                </tr>
                              )
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {editing && (
        <ExecutivePermissionsModal
          executive={editing}
          fields={PRIVILEGE_FIELDS}
          onClose={() => setEditing(null)}
          onSaved={() => fetchExecutives()}
        />
      )}
    </Layout>
  )
}

export default MultiLogin
